import { useEffect, useRef } from "react";
import { Form, InputNumber } from "antd";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import { getOptionsFromRefList } from "@/util/common_function";
import useActiveWarrant from "@/hooks/useActiveWarrant";

const toDayjs = (val) => {
  if (!val) return null;
  const d = dayjs(val);
  return d.isValid() ? d : null;
};

const WarrantBondInformation = () => {
    const [form] = Form.useForm();
    const { activeWarrant, bookingsFieldsEnabled, patch } = useActiveWarrant();
    const initializedRef = useRef(false);
    const prevWarrantIdRef = useRef(null);

    // Reset init guard when warrant changes
    useEffect(() => {
        const currentId = activeWarrant?.warrantId ?? activeWarrant?.warrantID ?? null;
        if (currentId !== prevWarrantIdRef.current) {
            initializedRef.current = false;
            prevWarrantIdRef.current = currentId;
        }
    }, [activeWarrant?.warrantId, activeWarrant?.warrantID]);

    // Initialise form ONCE when activeWarrant first loads
    useEffect(() => {
        if (!activeWarrant) return;
        if (initializedRef.current) return;
        initializedRef.current = true;

        form.setFieldsValue({
            bondType: activeWarrant?.bondType ?? undefined,
            bondAmount: activeWarrant?.bondAmount ? Number(activeWarrant.bondAmount) : undefined,
            bondDate: toDayjs(activeWarrant?.bondDate),
            bondedBy: activeWarrant?.bondedBy ?? "",
            bondPhone: activeWarrant?.bondPhone ?? "",
            bondStreet1: activeWarrant?.bondStreet1 ?? "",
            bondStreet2: activeWarrant?.bondStreet2 ?? "",
            bondCity: activeWarrant?.bondCity ?? "",
            bondState: activeWarrant?.bondState ?? undefined,
            bondZip: activeWarrant?.bondZip ?? "",
        });
    }, [activeWarrant]);

    const patchField = (key, val) => patch({ [key]: val });

    const handleBondAmountKeyDown = (e) => {
        const { key, ctrlKey, metaKey } = e;
        const allowedControlKeys = [
            "Backspace", "Delete", "ArrowLeft", "ArrowRight",
            "ArrowUp", "ArrowDown", "Tab", "Enter",
            "Home", "End", "Escape", "CapsLock", "Shift", "Control",
        ];
        if (allowedControlKeys.includes(key) || ctrlKey || metaKey) return;
        if (key === "." && !e.target.value.includes(".")) return;
        if (!/^[0-9]$/.test(key)) {
            e.preventDefault();
        }
    };

    const bondTypeOptions = getOptionsFromRefList( "BondType", false, false, true);

    return (
        <>
            <StyledSectionHeading marginTop="20px">
                <span>Bond Information</span>
            </StyledSectionHeading>

            <Form form={form} disabled={!bookingsFieldsEnabled}>
                <StyledGridRow gutter={16}>
                    {/* ── Left column ── */}
                    <StyledGridCol lg={12}>
                        <StyledGridRow gutter={16}>
                            <StyledGridCol lg={12}>
                                <FormItem label="Bond Type" name="bondType">
                                    <Select
                                        options={bondTypeOptions}
                                        placeholder="Bond Type"
                                        variant="outlined"
                                        onChange={(v) => {
                                            const selectedBondType = bondTypeOptions.find(
                                                (item) => item.value === v
                                            );

                                            patchField( "bondType", selectedBondType?.label ?? v);
                                        }}
                                        disabled={!bookingsFieldsEnabled}
                                    />
                                </FormItem>
                            </StyledGridCol>

                            <StyledGridCol lg={12}>
                                <FormItem label="Bond $" name="bondAmount">
                                    <InputNumber
                                        style={{ width: "100%", backgroundColor: "#fce8ed" }}
                                        className="field--pink"
                                        prefix="$"
                                        formatter={(value) =>
                                            value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                                        }
                                        parser={(value) => value?.replace(/[^\d.]/g, "")}
                                        precision={2}
                                        min={0}
                                        disabled={!bookingsFieldsEnabled}
                                        onKeyDown={handleBondAmountKeyDown}
                                        onBlur={() => patchField("bondAmount", form.getFieldValue("bondAmount") ?? null)}
                                    />
                                </FormItem>
                            </StyledGridCol>

                            <StyledGridCol lg={24}>
                                <FormItem label="Street 1" name="bondStreet1">
                                    <TextField
                                        inputType="textarea"
                                        onBlur={(e) => patchField("bondStreet1", e.target.value)}
                                    />
                                </FormItem>
                            </StyledGridCol>

                            <StyledGridCol lg={12}>
                                <FormItem label="City" name="bondCity">
                                    <TextField
                                        onBlur={(e) => patchField("bondCity", e.target.value)}
                                    />
                                </FormItem>
                            </StyledGridCol>

                            <StyledGridCol lg={12}>
                                <FormItem label="State" name="bondState">
                                    <Select
                                    options={getOptionsFromRefList("USState")}
                                    onChange={(v) => patchField("bondState", v?.value ?? v)}
                                    disabled={!bookingsFieldsEnabled}
                                    />
                                </FormItem>
                            </StyledGridCol>
                        </StyledGridRow>
                    </StyledGridCol>

                    {/* ── Right column ── */}
                    <StyledGridCol lg={12}>
                    <StyledGridRow gutter={16}>
                        <StyledGridCol lg={12}>
                            <FormItem label="Bond Date" name="bondDate">
                                <CommonDatePicker
                                    showTime={false}
                                    format="MM/DD/YYYY"
                                    onChange={(val) =>
                                        patchField(
                                        "bondDate",
                                        val ? dayjs(val).toISOString() : null
                                        )
                                    }
                                    disabled={!bookingsFieldsEnabled}
                                />
                            </FormItem>
                        </StyledGridCol>
                        <StyledGridCol lg={12}>
                            <FormItem label="Bondsman Name" name="bondedBy">
                                <TextField
                                    onBlur={(e) => patchField("bondedBy", e.target.value)}
                                />
                            </FormItem>
                        </StyledGridCol>

                        <StyledGridCol lg={24}>
                            <FormItem label="Street 2" name="bondStreet2">
                                <TextField
                                    inputType="textarea"
                                    onBlur={(e) => patchField("bondStreet2", e.target.value)}
                                />
                            </FormItem>
                        </StyledGridCol>

                        <StyledGridCol lg={12}>
                            <FormItem label="Zip Code" name="bondZip">
                                <TextField
                                    onBlur={(e) => patchField("bondZip", e.target.value)}
                                />
                            </FormItem>
                        </StyledGridCol>
                        <StyledGridCol lg={12}>
                            <FormItem label="Phone" name="bondPhone">
                                <TextField
                                    onBlur={(e) => patchField("bondPhone", e.target.value)}
                                />
                            </FormItem>
                        </StyledGridCol>
                    </StyledGridRow>
                    </StyledGridCol>
                </StyledGridRow>
            </Form>
        </>
    );
};

export default WarrantBondInformation;